import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { LayoutDashboard, LogOut, CreditCard, SlidersHorizontal, BarChart3, Menu, X } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = user
    ? [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
        { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing-link" },
        { to: "/agency", label: "Settings", icon: SlidersHorizontal, testid: "nav-agency-link" },
        ...(user.is_admin ? [{ to: "/admin", label: "Analytics", icon: BarChart3, testid: "nav-admin-link", accent: true }] : []),
      ]
    : [];

  return (
    <header className="fixed top-0 w-full z-50 bg-[#030812]/80 backdrop-blur-xl border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <>
              {links.map(({ to, label, icon: Icon, testid, accent }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={`hidden sm:flex items-center gap-2 ${accent ? "text-cyanbrand" : "text-slate-300"} hover:text-white px-3 py-2 rounded-md transition-colors text-sm`}
                >
                  <Icon size={18} strokeWidth={1.75} /> {label}
                </Link>
              ))}
              <NotificationBell />
              <button
                data-testid="nav-logout-button"
                onClick={() => { logout(); navigate("/"); }}
                className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-white px-3 py-2 rounded-md transition-colors text-sm border border-slate-700 hover:border-cyanbrand"
              >
                <LogOut size={16} strokeWidth={1.75} /> Sign out
              </button>
              {/* mobile hamburger */}
              <button
                data-testid="nav-mobile-toggle"
                onClick={() => setOpen((o) => !o)}
                className="sm:hidden flex items-center justify-center w-10 h-10 rounded-md text-slate-200 border border-slate-700 hover:border-cyanbrand transition-colors"
                aria-label="Menu"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-slate-300 hover:text-white px-3 sm:px-4 py-2 rounded-md transition-colors text-sm"
              >
                Log in
              </Link>
              <Link
                to="/signup"
                data-testid="nav-signup-link"
                className="bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold px-4 py-2 rounded-md transition-all text-sm whitespace-nowrap"
                style={{ boxShadow: "0 0 15px rgba(0,229,255,0.3)" }}
              >
                Get started
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* mobile dropdown menu */}
      {user && open && (
        <div className="sm:hidden bg-[#0C1424] border-b border-slate-800 px-4 py-3 space-y-1" data-testid="nav-mobile-menu">
          {links.map(({ to, label, icon: Icon, testid, accent }) => (
            <Link
              key={to}
              to={to}
              data-testid={`m-${testid}`}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 ${accent ? "text-cyanbrand" : "text-slate-200"} hover:bg-[#162238] px-3 py-3 rounded-md transition-colors text-base`}
            >
              <Icon size={20} strokeWidth={1.75} /> {label}
            </Link>
          ))}
          <button
            data-testid="m-nav-logout-button"
            onClick={() => { setOpen(false); logout(); navigate("/"); }}
            className="w-full flex items-center gap-3 text-slate-200 hover:bg-[#162238] px-3 py-3 rounded-md transition-colors text-base border-t border-slate-800 mt-2 pt-3"
          >
            <LogOut size={20} strokeWidth={1.75} /> Sign out
          </button>
        </div>
      )}
    </header>
  );
}
